import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "@/components/sections/Nav";
import Footer from "@/components/sections/Footer";
import { getPostBySlug, POSTS } from "@/data/blog";
import {
  ArrowLeft,
  ArrowUpRight,
  Linkedin,
  Facebook,
  Instagram as InstagramIcon,
  Link as LinkIcon,
} from "lucide-react";

// X (Twitter) and WhatsApp glyphs aren't in lucide by default — inline SVGs kept
// visually consistent with lucide's 1.5px stroke, 24×24 viewBox.
function XIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2H21.5l-7.5 8.573L22.75 22H16.08l-5.214-6.83L4.9 22H1.64l8.02-9.164L1.25 2h6.83l4.713 6.231L18.244 2Zm-1.14 18h1.8L7.02 4H5.09l12.014 16Z" />
    </svg>
  );
}

function WhatsAppIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.297-.347.446-.52.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.075-.792.372-.272.297-1.04 1.016-1.04 2.478 0 1.462 1.065 2.876 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.413-.074-.124-.272-.198-.57-.347Zm-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.437-9.884 9.891-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.889 9.884Zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

function ShareRow({ post }) {
  const shareUrl = `https://www.indoorgreens.in/journal/${post.slug}`;
  const text = `${post.title} — ${post.subtitle || "Indoor Greens Journal"}`;
  const encoded = {
    url: encodeURIComponent(shareUrl),
    text: encodeURIComponent(text),
    both: encodeURIComponent(`${text} ${shareUrl}`),
  };

  const targets = [
    {
      key: "linkedin",
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded.url}`,
      Icon: Linkedin,
    },
    {
      key: "x",
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?url=${encoded.url}&text=${encoded.text}`,
      Icon: XIcon,
    },
    {
      key: "facebook",
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded.url}`,
      Icon: Facebook,
    },
    {
      key: "whatsapp",
      label: "Share on WhatsApp",
      href: `https://wa.me/?text=${encoded.both}`,
      Icon: WhatsAppIcon,
    },
  ];

  const copyForInstagram = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it into your Instagram story or bio.");
    } catch {
      toast.error("Couldn't copy the link. Long-press the URL bar to copy it.");
    }
  };

  return (
    <div
      data-testid="share-row"
      className="flex flex-wrap items-center gap-4 sm:gap-5"
    >
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
        Share
      </span>
      {targets.map(({ key, label, href, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          data-testid={`share-${key}`}
          className="inline-flex items-center justify-center w-10 h-10 border border-[#E5E1DB] text-[#2D332B] hover:bg-[#2D332B] hover:text-[#F9F8F6] hover:border-[#2D332B] rounded-sm transition-colors duration-300"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        type="button"
        onClick={copyForInstagram}
        aria-label="Copy link for Instagram"
        data-testid="share-instagram"
        className="inline-flex items-center justify-center w-10 h-10 border border-[#E5E1DB] text-[#2D332B] hover:bg-[#2D332B] hover:text-[#F9F8F6] hover:border-[#2D332B] rounded-sm transition-colors duration-300"
      >
        <InstagramIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={copyForInstagram}
        aria-label="Copy link"
        data-testid="share-copy"
        className="inline-flex items-center gap-2 pl-3 pr-3 h-10 border border-[#E5E1DB] text-[10px] tracking-[0.25em] uppercase text-[#5C6658] hover:text-[#2D332B] hover:border-[#2D332B] rounded-sm transition-colors"
      >
        <LinkIcon className="w-3.5 h-3.5" /> Copy link
      </button>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function BlogBlock({ block }) {
  switch (block.type) {
    case "lead":
      return (
        <p className="mt-10 mb-6 font-[Outfit] font-light text-[22px] sm:text-[26px] leading-[1.4] text-[#2D332B]">
          {block.text}
        </p>
      );
    case "h2":
      return (
        <h2 className="mt-16 sm:mt-20 mb-4 font-[Outfit] font-light text-[26px] sm:text-[32px] leading-[1.15] text-[#2D332B] tracking-[-0.01em]">
          {block.text}
        </h2>
      );
    case "p":
      return (
        <p className="my-5 text-[16px] sm:text-[17px] leading-[1.8] text-[#3d4238]">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="my-6 space-y-3 pl-1">
          {block.items.map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-4 text-[16px] leading-[1.75] text-[#3d4238]"
            >
              <span className="mt-3 w-1.5 h-1.5 rounded-full bg-[#8A9A86] shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "pullquote":
      return (
        <blockquote className="my-12 sm:my-16 border-l-2 border-[#8A9A86] pl-6 sm:pl-8 py-2">
          <p className="font-[Outfit] italic font-light text-[24px] sm:text-[30px] leading-[1.25] text-[#2D332B]">
            {block.text}
          </p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function JournalPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Inject BlogPosting JSON-LD into <head> for this article only.
  useEffect(() => {
    if (!post) return undefined;
    const ld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover ? [post.cover] : undefined,
      datePublished: post.published_at,
      dateModified: post.published_at,
      author: [{ "@type": "Person", name: post.author }],
      publisher: {
        "@type": "Organization",
        name: "Indoor Greens",
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/wingman/85cf05f2-d849-40c8-9fc9-80449c196070/attachments/71b336acd64a487ea8f6710e443d86c9_FullColor_TransparentBg_1280x1024_72dpi.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://www.indoorgreens.in/journal/${post.slug}`,
      },
      keywords: (post.tags || []).join(", "),
      articleSection: "Journal",
      inLanguage: "en-IN",
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-blogposting", post.slug);
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    // Also set page title + meta description dynamically for social/AI previews
    const prevTitle = document.title;
    document.title = `${post.title} — Indoor Greens Journal`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", post.excerpt);
    return () => {
      el.remove();
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [post]);

  if (!post) return <Navigate to="/journal" replace />;

  const others = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <main
      data-testid="journal-post-page"
      className="bg-[#F9F8F6] text-[#2D332B] min-h-screen"
    >
      <Nav />

      {/* Hero */}
      <section className="pt-40 pb-14 sm:pt-48 sm:pb-16">
        <div className="max-w-[820px] mx-auto px-6 sm:px-10">
          <Link
            to="/journal"
            data-testid="journal-back-link"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#5C6658] hover:text-[#2D332B] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> The Journal
          </Link>

          <div className="mt-10 flex flex-wrap gap-3">
            {post.tags?.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.25em] uppercase text-[#5C6658] border border-[#E5E1DB] px-3 py-1 rounded-sm"
              >
                {t}
              </span>
            ))}
          </div>

          <h1
            data-testid="journal-post-title"
            className="mt-6 font-[Outfit] font-extralight text-[#2D332B] text-[36px] sm:text-[54px] leading-[1.02] tracking-[-0.02em]"
          >
            {post.title}
          </h1>
          {post.subtitle && (
            <p className="mt-4 font-[Outfit] italic font-light text-[20px] sm:text-[26px] text-[#8A9A86] leading-[1.3]">
              {post.subtitle}
            </p>
          )}

          <div className="mt-10 flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-[#5C6658]">
            <span>{post.author}</span>
            <span className="w-1 h-1 rounded-full bg-[#B4C2AF]" />
            <span>{formatDate(post.published_at)}</span>
            <span className="w-1 h-1 rounded-full bg-[#B4C2AF]" />
            <span>{post.reading_minutes} min read</span>
          </div>

          <div className="mt-8">
            <ShareRow post={post} />
          </div>
        </div>
      </section>

      {/* Cover image */}
      {post.cover && (
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <div className="relative aspect-[16/9] overflow-hidden rounded-sm">
            <img
              src={post.cover}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <article
        data-testid="journal-post-body"
        className="max-w-[720px] mx-auto px-6 sm:px-10 py-16 sm:py-24"
      >
        {post.body.map((block, i) => (
          <BlogBlock key={i} block={block} />
        ))}

        <div className="mt-16 pt-10 border-t border-[#E5E1DB]">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
            Share this essay
          </p>
          <div className="mt-5">
            <ShareRow post={post} />
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-[#E5E1DB]">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
            Written by
          </p>
          <p className="mt-2 font-[Outfit] font-light text-[20px] text-[#2D332B]">
            {post.author}
          </p>
          <p className="mt-4 text-[14px] leading-relaxed text-[#5C6658] max-w-md">
            Indoor Greens is a luxury biophilic design and maintenance studio
            based in Mumbai, working with residences, hotels and offices across
            India.
          </p>
        </div>
      </article>

      {/* Other essays */}
      {others.length > 0 && (
        <section className="pb-32 border-t border-[#E5E1DB] pt-20 sm:pt-24">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-10">
              Also in the Journal
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/journal/${p.slug}`}
                  className="group block border border-[#E5E1DB] rounded-sm overflow-hidden hover:border-[#8A9A86] transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]"
                    />
                  </div>
                  <div className="p-7">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                      {formatDate(p.published_at)}
                    </p>
                    <h3 className="mt-3 font-[Outfit] font-light text-[22px] text-[#2D332B] leading-[1.15]">
                      {p.title}
                    </h3>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#2D332B] group-hover:text-[#8A9A86]">
                      Read essay <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
